import { useQuery } from '@apollo/client/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ProgramFormData } from './ProgramFormConfig';
import { defaultProgramFormValues, getFieldsToValidate, programFormSchema } from './ProgramFormConfig';
import { useProgramFormState, type FormMode } from './useProgramFormState';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import InputField from '@/components/fields/InputField';
import RadioField from '@/components/fields/RadioField';
import SelectField from '@/components/fields/SelectField';
import TextareaField from '@/components/fields/TextareaField';
import { ProgramOccurrence } from '@/gql/enums';
import type { ProgramListItemDataFragment } from '@/gql/graphql';
import { FetchCourses } from '@/pages/GroupManagementPage/shared/Courses.graphql';

export interface ProgramFormProps {
    mode: FormMode;
    open: boolean;
    title: string;
    program?: ProgramListItemDataFragment | null;
    onSubmit: (data: ProgramFormData) => Promise<void>;
    onClose: () => void;
}

const ProgramFormContainer = ({ mode, open, title, onSubmit, program, onClose }: ProgramFormProps) => {
    const { t } = useTranslation('programManagementPage');
    const { t: tGlobal } = useTranslation('common');

    const occurrenceOptions = [
        { value: ProgramOccurrence.NotRepeating, label: t('fields.occurrenceValue.NotRepeating') },
        { value: 'Standard', label: t('fields.occurrenceValue.Standard'), disabled: true },
        { value: ProgramOccurrence.Custom, label: t('fields.occurrenceValue.Custom') },
    ];

    const { data } = useQuery(FetchCourses, {
        fetchPolicy: 'cache-and-network',
        variables: {},
        skip: !open || mode === 'edit',
    });

    const coursesOptions = useMemo(() => {
        const coursesOptions = (data?.courseInstances?.items || []).map(course => ({
            value: course.id,
            label: course.name,
        }));

        return coursesOptions;
    }, [data?.courseInstances.items]);

    // Initialize form state
    const initialData = useMemo(() => {
        return program
            ? {
                  name: program.name || '',
                  description: program.description || '',
                  courseInstanceId: program.courseInstanceId || '',
                  occurrence: program.occurrence || ProgramOccurrence.NotRepeating,
              }
            : undefined;
    }, [program]);

    const { state, actions } = useProgramFormState(mode, initialData);
    const form = useForm<ProgramFormData>({
        mode: 'onChange',
        resolver: zodResolver(programFormSchema),
        defaultValues: state.formData,
    });

    useEffect(() => {
        if (open) {
            actions.initialize(initialData || defaultProgramFormValues);
            form.reset(initialData);
        }
    }, [open, actions, form, initialData]);

    const handleClose = useCallback(() => {
        form.reset(defaultProgramFormValues);
        actions.reset();
        onClose();
    }, [onClose, actions, form]);

    const handleSubmit = useCallback(async () => {
        const fieldsToValidate = getFieldsToValidate();
        const isValid = await form.trigger(fieldsToValidate);
        if (!isValid) {
            return;
        }
        const formData = form.getValues();
        try {
            actions.submitStart();
            await onSubmit(formData);
            actions.submitSuccess();
            handleClose();
        } catch (error) {
            actions.submitError();
            if (error && typeof error === 'object' && 'fields' in error) {
                const fields = error.fields as { field: string; message: string }[];
                console.error('Validation errors:', fields);
                fields.forEach(err => {
                    if (err.field && err.message) {
                        form.setError(err.field as keyof ProgramFormData, {
                            type: 'manual',
                            message: err.message,
                        });
                    }
                });
            }
        }
    }, [form, onSubmit, actions, handleClose]);

    const modalFooter = (
        <div className="flex items-center justify-end gap-2.5">
            <Button variant="secondary" onClick={handleClose} disabled={state.isSubmitting}>
                {tGlobal('modal.cancel')}
            </Button>
            <Button variant="primary" type="submit" onClick={handleSubmit} disabled={state.isSubmitting}>
                {mode === 'edit' ? t('modalEditProgram.updateButton') : t('modalCreateProgram.createButton')}
            </Button>
        </div>
    );

    const modalWidth = mode === 'edit' ? '!w-[512px] !max-w-[512px]' : '!w-[800px] !max-w-[800px]';

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={handleClose}
                title={title}
                largeTitle
                subtitle={mode !== 'edit' ? t('modalCreateProgram.subtitle') : undefined}
                subtitleClassName="mt-1"
                footer={modalFooter}
                showCloseButton={false}
                className={modalWidth}
            >
                <div className="flex flex-col">
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="mb-5 flex flex-col gap-5">
                        <InputField
                            label={t('fields.name.label')}
                            name="name"
                            placeholder={t('fields.name.placeholder')}
                            required
                            disabled={false}
                            labelSize="base"
                            labelWeight="semibold"
                        />
                        <TextareaField
                            label={t('fields.description.label')}
                            name="description"
                            placeholder={t('fields.description.placeholder')}
                            rows={4}
                            labelSize="base"
                            labelWeight="semibold"
                        />
                        {mode !== 'edit' && (
                            <>
                                <SelectField
                                    name="courseInstanceId"
                                    label={t('fields.courses.label')}
                                    options={coursesOptions}
                                    required
                                    placeholder={t('fields.courses.placeholder')}
                                    labelSize="base"
                                    labelWeight="semibold"
                                />
                                <RadioField
                                    name="occurrence"
                                    label={t('fields.occurrence.label')}
                                    options={occurrenceOptions}
                                    defaultValue={ProgramOccurrence.NotRepeating}
                                    required
                                />
                            </>
                        )}
                    </form>
                </div>
            </Modal>
        </FormProvider>
    );
};

export default ProgramFormContainer;
